import React, { useMemo, useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import { css } from '@emotion/core'
import { Card, H2, H6, Separator, Expander, ExpanderHeader, ExpanderContent } from '@fs/zion-ui'
import { useLocation } from '@fs/zion-router'
import { TRAINING_NAV_ITEMS } from './PlaceEditorTrainingNav'

import transcript2Introduction from '../assets/2_Introduction_FS_Places_transcript.json'
import transcript3KeyConcepts from '../assets/3_Key_Concepts_FS_Places_transcript.json'
import transcript4Searching from '../assets/4_Searching_for_Places_transcript.json'
import transcript5Editing from '../assets/5_Editing_Entries_FS_Places_transcript.json'
import transcript5aParentPlace from '../assets/5a_Parent_Place_transcript.json'
import transcript5bPlaceType from '../assets/5b_Place_Type_transcript.json'
import transcript5cNativeLanguage from '../assets/5c_Native_Language_transcript.json'
import transcript5dCertification from '../assets/5d_Certification_Status_transcript.json'
import transcript5eHistoricalPeriods from '../assets/5e_Historical_Periods_transcript.json'
import transcript5fStartEndYear from '../assets/5f_Start_End_Year_transcript.json'
import transcript5gCoordinates from '../assets/5g_Coordinates_transcript.json'
import transcript5hNames from '../assets/5h_Names_transcript.json'
import transcript5iResearchLinks from '../assets/5i_Research_Links_transcript.json'
import transcript5jCitations from '../assets/5j_Citations_transcript.json'
import transcript5kHistoricalInfo from '../assets/5k_Historical_Information_transcript.json'
import transcript5lRelatedPlaces from '../assets/5l_Related_Places_transcript.json'
import transcript6CreatingEntries from '../assets/6_Creating_New_Entries_FS_Places_transcript.json'

const buildTranscriptMap = () => {
  const allItems = TRAINING_NAV_ITEMS.flatMap((item) => [item, ...(item.subItems || [])])
  const transcripts = [
    transcript2Introduction,
    transcript3KeyConcepts,
    transcript4Searching,
    transcript5Editing,
    transcript5aParentPlace,
    transcript5bPlaceType,
    transcript5cNativeLanguage,
    transcript5dCertification,
    transcript5eHistoricalPeriods,
    transcript5fStartEndYear,
    transcript5gCoordinates,
    transcript5hNames,
    transcript5iResearchLinks,
    transcript5jCitations,
    transcript5kHistoricalInfo,
    transcript5lRelatedPlaces,
    transcript6CreatingEntries,
  ]

  const map = {}
  for (let index = 0; index < allItems.length; index++) {
    const item = allItems[index]
    if (item.pdfFile && transcripts[index]) {
      map[item.pdfFile] = transcripts[index]
    }
  }
  return map
}

const TRANSCRIPT_MAP = buildTranscriptMap()

const containerStyles = css`
  display: flex;
  flex-direction: column;
  width: 100%;
  min-height: calc(100vh - 160px);
`

const pdfContainerStyles = css`
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  margin-bottom: 0;
`

function findItem(id) {
  if (!id) return TRAINING_NAV_ITEMS[0] || null
  const top = TRAINING_NAV_ITEMS.find((i) => i.id === id)
  if (top) return top

  const subItem = TRAINING_NAV_ITEMS.flatMap((parent) => parent.subItems || []).find((s) => s.id === id)
  if (subItem) return subItem

  return TRAINING_NAV_ITEMS[0] || null
}

function PdfViewer({ src, title }) {
  const [t] = useTranslation()

  return (
    <div
      css={css`
        height: 600px;
        display: flex;
        flex-direction: column;
        overflow: hidden;
      `}
    >
      <iframe
        title={title}
        aria-label={t(
          'editor-handbook.place-editor-training-content.training-document.aria-label',
          '{title} training document',
          { title }
        )}
        src={src}
        style={{
          flex: 1,
          width: '100%',
          height: '100%',
          border: 'none',
          background: '#fff',
        }}
      />
      <div
        style={{
          marginTop: 4,
          fontSize: 12,
          flexShrink: 0,
        }}
      >
        {t(
          'editor-handbook.place-editor-training-content.pdf-fallback-prefix.message',
          'If the PDF does not display, '
        )}
        <a href={src} target="_blank" rel="noopener noreferrer">
          {t('editor-handbook.place-editor-training-content.open-new-tab.link', 'open it in a new tab')}
        </a>
        .
      </div>
    </div>
  )
}

PdfViewer.propTypes = {
  src: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
}

function TranscriptExpander({ pdfSrc }) {
  const [t] = useTranslation()
  const [transcript, setTranscript] = useState(null)

  useEffect(() => {
    if (!pdfSrc) {
      setTranscript(null)
      return
    }

    const transcriptData = TRANSCRIPT_MAP[pdfSrc]

    setTranscript(transcriptData)
  }, [pdfSrc])

  if (!transcript?.slides || transcript.slides.length === 0) {
    return null
  }

  return (
    <div style={{ marginTop: '0' }}>
      <Expander initiallyOpen={false}>
        <ExpanderHeader>
          <H6>{t('editor-handbook.place-editor-training-content.view-transcript.label', 'View Transcript')}</H6>
        </ExpanderHeader>
        <ExpanderContent>
          <div
            css={css`
              max-height: 400px;
              overflow-y: auto;
              overflow-x: hidden;
              padding: 16px;
              background: #ffffff;
              word-wrap: break-word;
              overflow-wrap: break-word;
            `}
          >
            {transcript.slides.map((slide) => (
              <div key={slide.slide} style={{ marginBottom: '24px' }}>
                {slide.title && (
                  <div style={{ fontWeight: 'bold', marginBottom: '8px' }}>
                    {t('editor-handbook.place-editor-training-content.slide.label', 'Slide')} {slide.slide}:{' '}
                    {slide.title}
                  </div>
                )}
                {slide.content && (
                  <div
                    style={{
                      whiteSpace: 'pre-wrap',
                      lineHeight: '1.6',
                      wordWrap: 'break-word',
                    }}
                  >
                    {slide.content}
                  </div>
                )}
              </div>
            ))}
          </div>
        </ExpanderContent>
      </Expander>
    </div>
  )
}

TranscriptExpander.propTypes = {
  pdfSrc: PropTypes.string,
}

export default function PlaceEditorTrainingContent() {
  const [t] = useTranslation()
  const location = useLocation()

  const hashId = useMemo(() => {
    const hash = location?.hash || ''
    return hash ? hash.substring(1) : ''
  }, [location?.hash])

  const currentItem = useMemo(() => {
    return findItem(hashId)
  }, [hashId])

  const pdfSrc = currentItem?.pdfFile || null

  return (
    <div id="place-editor-training-content" css={containerStyles}>
      <div css={pdfContainerStyles}>
        <Card margins="md">
          <H2 size="H4" centered>
            {currentItem?.title || t('editor-handbook.place-editor-training-content.training.title', 'Training')}
          </H2>
          <Separator size="sm" />
          {pdfSrc ? (
            <PdfViewer src={pdfSrc} title={currentItem?.title || 'Training PDF'} />
          ) : (
            <p style={{ margin: 0 }}>
              {t('editor-handbook.place-editor-training-content.no-pdf-found.message', 'No PDF found for this topic.')}
            </p>
          )}
          {pdfSrc && (
            <>
              <Separator size="sm" />
              <TranscriptExpander pdfSrc={pdfSrc} />
            </>
          )}
        </Card>
      </div>
    </div>
  )
}
